<template>
  <sticky-header-layout>
    <!-- Header -->
    <template #header>
      <div class="action-container justify-content-end">
        <span>
          <el-button
            :disabled="isSaving"
            class="cancel-button"
            size="mini"
            type="warning"
            icon="el-icon-close"
            @click="handleCancel"
          >{{ $t('button.cancel') }}</el-button>
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            class="save-button"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >{{ $t('button.save') }}</el-button>
        </span>
      </div>
    </template>
    <!-- Header -->

    <div v-loading="isLoading">
      <generic-form
        ref="form"
        :attributes="formAttributes"
        :form="form"
        :form-template="formTemplate"
        class="pt-3"
      >
        <template
          v-for="(row, index) in formTemplate"
        >
          <div
            :key="`row-${row.key}`"
            :style="{ order: index }"
          >
            <template v-if="row.key === 'brands'">
              <el-form-item :label="$t('common.brands')">
                <brand-selector
                  v-model="form[row.key]"
                />
              </el-form-item>
            </template>

            <template v-else-if="row.key === 'categories'">
              <el-form-item :label="$t('common.categories')">
                <category-selector
                  v-model="form[row.key]"
                />
              </el-form-item>
            </template>

            <template v-else-if="row.key === 'stores'">
              <el-form-item :label="$t('common.stores')">
                <store-selector
                  v-model="form[row.key]"
                  class="w-100"
                />
              </el-form-item>
            </template>

            <template v-else-if="row.key === 'product_ids'">
              <product-selector
                v-model="form[row.key]"
                :edit-mode="isEditMode"
                class="px-2 pb-2"
              />
            </template>
          </div>
        </template>

        <div
          v-if="isEditMode"
          style="order: 999"
        >
          <div class="d-flex px-2">
            <span class="font-weight-500 pr-3 pb-3">SEO</span>
            <a
              :href="seoLink"
              class="seo-link"
              target="_blank"
            >
              <font-awesome-icon icon="external-link-alt" />
            </a>
          </div>
        </div>
      </generic-form>
    </div>
  </sticky-header-layout>
</template>

<script>
import GenericForm from '../../../components/form/Form.vue'
import StoreSelector from '../../../components/selectors/StoreSelector.vue'
import BrandSelector from '../../../components/selectors/BrandSelector.vue'
import CategorySelector from '../../../components/selectors/CategorySelector.vue'
import ProductSelector from '../../../components/form/ProductSelector.vue'

import {
  FormRules,
  ServerToFormData,
  FormToServerFormat,
} from '../../../utils/form'
import { feedFormTemplate } from '../../../const/trending/feed'
import TrendingFeedsService from '../../../api/trending-feeds'
import { getRandomHash } from '../../../utils/helper'

export default {
  name: 'TrendingFeedDetail',
  components: {
    GenericForm,
    BrandSelector,
    CategorySelector,
    StoreSelector,
    ProductSelector,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  data() {
    const defaultForm = {
      slug: '',
      slug_zh_hk: null,
      slug_zh_cn: null,
      slug_th_th: null,
      slug_zh_tw: null,
      brands: [],
      categories: [],
      stores: [],
      genders: [],
      min_price: null,
      max_price: null,
      discount_from: null,
      product_ids: this.isEditMode ? [] : '',
      available_countries: ['ca', 'us', 'uk', 'sg', 'hk', 'my', 'ph', 'in', 'th', 'au', 'tw'],
    }
    return {
      isEdited: false,
      isLoading: false,
      isSaving: false,
      formTemplate: feedFormTemplate,
      form: defaultForm,
      originalSlug: '',
    }
  },
  computed: {
    isEditMode() {
      return !!this.id
    },
    formAttributes() {
      return {
        disabled: this.isSaving,
        labelWidth: '140px',
        rules: {
          slug: [FormRules.required],
        },
      }
    },
    seoLink() {
      return `/seo/edit/type/3/key/${this.originalSlug}`
    },
  },
  watch: {
    form: {
      handler() {
        this.isEdited = true
      },
      deep: true,
    },
    'form.slug': function _(v) {
      this.form.slug = v ? v.toLowerCase() : ''
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.getDetail(this.id)
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSaving) {
      return next(false)
    }
    if (!this.isEdited) {
      return next()
    }

    this.$confirm(this.$t('message.leaveWithUnsavedChanges'), this.$t('common.warning'), {
      confirmButtonText: this.$t('button.leave'),
      cancelButtonText: this.$t('button.cancel'),
      type: 'warning',
    }).then(() => next()).catch(() => next(false))

    return next(false)
  },
  methods: {
    goToListPage() {
      this.$router.push({ path: '/trending-feeds/list' })
    },
    handleCancel() {
      this.goToListPage()
    },
    handleSave() {
      this.$refs.form.$refs.form.validate().then(() => {
        const form = {
          ...this.form,
          available_countries: FormToServerFormat.getCountryAvailabilityMap(this.form.available_countries),
        }
        if (Array.isArray(this.form.product_ids)) {
          form.product_ids = this.form.product_ids.map(item => item.id)
        } else {
          form.product_ids = this.form.product_ids.split(',')
        }

        this.isSaving = true
        if (this.isEditMode) {
          this.editFeed(form)
        } else {
          this.addFeed(form)
        }
      }).catch((error) => {
        console.error(error);
        this.$alert(this.$t('error.pleaseFillInAndCheckErrors'), this.$t('common.oops'), {
          confirmButtonText: this.$t('button.ok'),
        });
      })
    },
    handleSaveSuccess() {
      this.isSaving = false
      this.isEdited = false
      this.goToListPage()
    },
    handleSaveFail(error) {
      let reason = (((error || {}).response || {}).data || {}).reason || ''
      if (reason === `SQLSTATE[23000]: Integrity constraint violation: 1062 Duplicate entry '${this.form.slug}' for key 'slug'`) {
        reason = 'Duplicated "slug" with other feeds. Please use a different "slug".'
      }
      if (!reason && error && error.message && this.$te(`error.${error.message}`)) {
        reason = this.$t(`error.${error.message}`)
      }
      this.isSaving = false
      this.$alert(`${reason || error}`, this.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: this.$t('button.ok'),
      })
    },
    async getDetail(id) {
      this.isLoading = true
      try {
        const { data } = await TrendingFeedsService.getDetail(id)
        data.available_countries = ServerToFormData.getAvailableCountriesList(data.available_countries)
        data.product_ids = data.product_ids.map(item => ({ id: item, key: getRandomHash() }))
        this.form = data
        this.originalSlug = data.slug
        this.$nextTick(() => {
          this.isEdited = false
        })
      } catch (error) {
        console.error(error);
        this.$alert(this.$t('error.trendingFeedNotFound'), this.$t('common.error'), {
          type: 'error',
          confirmButtonText: this.$t('button.ok'),
        }).then(() => {
          this.goToListPage()
        })
      } finally {
        this.isLoading = false
      }
    },
    async editFeed(form) {
      try {
        await TrendingFeedsService.update(form, this.id)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async addFeed(form) {
      try {
        await TrendingFeedsService.create(form)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/form.scss";

.el-form-item {
  padding-bottom: 6px;
}

.table-img {
  width: unset;
}

::v-deep {
  .el-form-item__label {
    + .el-form-item__content {
      > *:not(.el-input):not(.el-textarea):not(.multiselect) {
        margin-top: 4px;
      }
    }
  }
  .el-table .el-table__row td {
    border: none;
    vertical-align: top;
  }
  .el-select__input.is-mini {
    height: auto;
  }
}
</style>
